import React, {useRef} from "react";
import Snackbar from "@mui/material/Snackbar";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import {useMenuTranslation} from "../menu/menuTranslationContext";
import {useInstallApp} from "./useInstallApp";
import {usePageScroll} from "./usePageScroll";
import {IosInstallDialog} from "./IosInstallDialog";
import {TOUCH_TARGET} from "./touchTarget";

/**
 * Offers to install DAMS as an app after login on phones and tablets (DE-1707).
 *
 * Never hides by itself. Tapping elsewhere, scrolling the page or Escape hides it for
 * this session, so it never keeps covering the page; "Ikke nå" hides it for 30 days.
 * Only the banner itself catches taps: the strip around it lets them through.
 */
export const InstallAppBanner = () => {
    const t = useMenuTranslation();
    const {showBanner, install, dismiss, hideBannerForSession, iosDialogOpen, closeIosDialog} = useInstallApp();
    const bannerRef = useRef(null);
    const focusBefore = useRef(null);

    const hide = () => {
        returnFocus(bannerRef.current, focusBefore.current);
        hideBannerForSession();
    };

    // While the iOS instructions are open, the banner must stay so focus can return to it.
    const canHide = showBanner && !iosDialogOpen;
    usePageScroll(canHide, bannerRef, hide);

    const rememberFocusBefore = (event) => {
        if (!bannerRef.current?.contains(event.relatedTarget)) {
            focusBefore.current = event.relatedTarget;
        }
    };

    return <>
        <Snackbar ref={bannerRef}
                  open={showBanner}
                  onClose={() => canHide && hide()}
                  anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                  message={t('installAppBannerMessage', 'Installer DAMS som app på denne enheten?')}
                  action={<BannerActions t={t} onInstall={install} onDismiss={dismiss}/>}
                  sx={{pointerEvents: 'none'}}
                  slotProps={{content: {role: 'status', sx: {pointerEvents: 'auto'}, onFocus: rememberFocusBefore}}}/>
        <IosInstallDialog open={iosDialogOpen} onClose={closeIosDialog}/>
    </>;
};

const BannerActions = ({t, onInstall, onDismiss}) => (
    <Stack direction="row" spacing={1}>
        <Button color="inherit" onClick={onDismiss} sx={TOUCH_TARGET}>{t('installAppNotNow', 'Ikke nå')}</Button>
        <Button variant="contained" onClick={onInstall} sx={TOUCH_TARGET}>{t('installAppInstall', 'Installer')}</Button>
    </Stack>
);

/** Moves focus off the closing banner: back to where the user was, or released if that is gone. */
const returnFocus = (banner, previous) => {
    if (!banner?.contains(document.activeElement)) {
        return;
    }
    previous?.focus();
    // The previous element may be gone or no longer focusable.
    if (banner.contains(document.activeElement)) {
        document.activeElement.blur();
    }
};
